import React, { useState, useEffect } from 'react';
import { useSales, useCustomers, useBatteryStock, useChargerStock } from '../hooks/useDatabase';
import SalesForm from './SalesForm';
import SalesList from './SalesList';
import SaleDetailsModal from './SaleDetailsModal';
import DeletionLogModal from './DeletionLogModal';
import ConfirmDeleteModal from './ConfirmDeleteModal';
import apiService from '../services/ApiService';

const SalePage = ({ currentUser }) => {
  const { sales, createSale, updateSale, deleteSale, forceDeleteSale, fetchSales } = useSales();
  const { customers, fetchCustomers } = useCustomers();
  const { batteryStock, fetchBatteryStock } = useBatteryStock();
  const { chargerStock, fetchChargerStock } = useChargerStock();

  const [showSalesForm, setShowSalesForm] = useState(false);
  const [editingSale, setEditingSale] = useState(null);
  const [filteredSales, setFilteredSales] = useState([]);
  const [searchFilters, setSearchFilters] = useState({
    saleId: '',
    customerName: '',
    dateFrom: '',
    dateTo: '',
    deliveryDateFrom: '',
    deliveryDateTo: '',
    status: 'all',
    productType: 'all'
  });
  const [showSerialPopup, setShowSerialPopup] = useState(false);
  const [selectedSerials, setSelectedSerials] = useState([]);
  const [pendingSaleData, setPendingSaleData] = useState(null);
  const [showQuotationDetailsModal, setShowQuotationDetailsModal] = useState(false);
  const [selectedQuotationForDetails, setSelectedQuotationForDetails] = useState(null);
  const [showConfirmDeleteModal, setShowConfirmDeleteModal] = useState(false);
  const [saleToDelete, setSaleToDelete] = useState(null);
  const [showDeletionLogModal, setShowDeletionLogModal] = useState(false);
  const [selectedSale, setSelectedSale] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  useEffect(() => {
    console.log('SalePage: Initial data fetch effect triggered');
    fetchSales();
    fetchCustomers();
    fetchBatteryStock();
    fetchChargerStock();
  }, [fetchSales, fetchCustomers, fetchBatteryStock, fetchChargerStock]);

  useEffect(() => {
    setFilteredSales(sales || []);
  }, [sales]);

  useEffect(() => {
    applyFilters();
  }, [searchFilters, sales]);

  // ฟังก์ชันรับข้อมูลจากหน้า QuotationPage
  useEffect(() => {
    const handleQuotationSale = (event) => {
      const { quotationData, selectedSerials, saleData } = event.detail;

      console.log('SalePage: Received quotation sale data:', { quotationData, selectedSerials, saleData });

      // ข้อมูลการขายถูกสร้างแล้วใน QuotationPage และส่งผ่าน API
      // เราแค่ต้องรีเฟรชข้อมูลการขายเพื่อแสดงผลใหม่
      fetchSales();
    };

    window.addEventListener('quotationSaleCreated', handleQuotationSale);
    return () => {
      window.removeEventListener('quotationSaleCreated', handleQuotationSale);
    };
  }, [currentUser]);

  const calculateWarrantyEndDate = (warrantyMonths, saleDate = null) => {
    if (!warrantyMonths) return '';
    try {
      const startDate = saleDate ? new Date(saleDate) : new Date();
      const endDate = new Date(startDate);
      endDate.setMonth(endDate.getMonth() + parseInt(warrantyMonths));

      return endDate.toLocaleDateString('th-TH', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Error calculating warranty end date:', error);
      return '-';
    }
  };

  const applyFilters = () => {
    if (!sales) return;

    let filtered = [...sales];

    // กรองตามเลขที่ใบขาย
    if (searchFilters.saleId) {
      filtered = filtered.filter(sale =>
        (sale.id || '').toString().includes(searchFilters.saleId) ||
        (sale.serialNumber || '').toLowerCase().includes(searchFilters.saleId.toLowerCase())
      );
    }

    // กรองตามชื่อลูกค้า
    if (searchFilters.customerName) {
      filtered = filtered.filter(sale =>
        (sale.customerName || '').toLowerCase().includes(searchFilters.customerName.toLowerCase())
      );
    }

    // กรองตามวันที่ขาย
    if (searchFilters.dateFrom) {
      filtered = filtered.filter(sale =>
        new Date(sale.saleDate) >= new Date(searchFilters.dateFrom)
      );
    }

    if (searchFilters.dateTo) {
      const dateTo = new Date(searchFilters.dateTo);
      dateTo.setHours(23, 59, 59, 999);
      filtered = filtered.filter(sale =>
        new Date(sale.saleDate) <= dateTo
      );
    }

    // กรองตามวันที่ส่งมอบ
    if (searchFilters.deliveryDateFrom) {
      filtered = filtered.filter(sale =>
        sale.delivery_date && new Date(sale.delivery_date) >= new Date(searchFilters.deliveryDateFrom)
      );
    }

    if (searchFilters.deliveryDateTo) {
      const dateTo = new Date(searchFilters.deliveryDateTo);
      dateTo.setHours(23, 59, 59, 999);
      filtered = filtered.filter(sale =>
        sale.delivery_date && new Date(sale.delivery_date) <= dateTo
      );
    }

    // กรองตามสถานะ
    if (searchFilters.status !== 'all') {
      filtered = filtered.filter(sale => sale.status === searchFilters.status);
    }

    // กรองตามประเภทสินค้า
    if (searchFilters.productType !== 'all') {
      filtered = filtered.filter(sale => {
        if (searchFilters.productType === 'battery') {
          return sale.productType === 'battery';
        } else if (searchFilters.productType === 'charger') {
          return sale.productType === 'charger';
        } else if (searchFilters.productType === 'mixed') {
          return sale.productType === 'mixed';
        }
        return true;
      });
    }

    // เรียงลำดับตามวันที่ขายล่าสุดมาก่อน
    filtered.sort((a, b) => {
      const dateA = new Date(a.saleDate);
      const dateB = new Date(b.saleDate);
      return dateB - dateA; // เรียงจากใหม่ไปเก่า
    });

    setFilteredSales(filtered);
  };

  const handleSearchChange = (e) => {
    const { name, value } = e.target;
    setSearchFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const clearFilters = () => {
    setSearchFilters({
      saleId: '',
      customerName: '',
      dateFrom: '',
      dateTo: '',
      deliveryDateFrom: '',
      deliveryDateTo: '',
      status: 'all',
      productType: 'all'
    });
  };

  const handleCreateSale = async (saleData) => {
    try {
      const result = await createSale(saleData);
      if (result.success) {
        setShowSalesForm(false);
        fetchSales();
        fetchBatteryStock(); // อัปเดตสต็อก
        fetchChargerStock();
      } else {
        alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล: ' + result.error);
      }
    } catch (error) {
      console.error('Error creating sale:', error);
      alert('เกิดข้อผิดพลาดในการบันทึกข้อมูล');
    }
  };

  const handleEditSale = (sale) => {
    setEditingSale(sale);
    setShowSalesForm(true);
  };

  const handleUpdateSale = async (id, saleData) => {
    try {
      const result = await updateSale(id, saleData);
      if (result.success) {
        setShowSalesForm(false);
        setEditingSale(null);
        fetchSales();
      } else {
        alert('เกิดข้อผิดพลาดในการแก้ไขข้อมูล: ' + result.error);
      }
    } catch (error) {
      console.error('Error updating sale:', error);
      alert('เกิดข้อผิดพลาดในการแก้ไขข้อมูล');
    }
  };

  const handleDeleteSale = async (id) => {
    try {
      const result = await deleteSale(id);
      if (result.success) {
        fetchSales();
        fetchBatteryStock(); // อัปเดตสต็อกคืน
        fetchChargerStock();
      } else {
        // Error handling is done in SalesList now
        throw new Error(result.error);
      }
    } catch (error) {
      console.error('Error deleting sale:', error);
      throw error; // Re-throw to be caught in SalesList
    }
  };

  const handleForceDelete = (sale) => {
    setSaleToDelete(sale);
    setShowConfirmDeleteModal(true);
  };

  const confirmForceDelete = async (reason) => {
    if (!saleToDelete) return;

    // Logic เดิมจากการใช้ prompt
    if (!reason.trim()) {
      alert('กรุณาระบุเหตุผลในการลบ');
      return;
    }

    try {
      const deletedBy = currentUser ? (currentUser.name || currentUser.username) : 'Unknown';
      const result = await forceDeleteSale(saleToDelete.id, reason, deletedBy);
      if (result.success) {
        fetchSales();
        fetchBatteryStock();
        fetchChargerStock();
        setShowConfirmDeleteModal(false);
        setSaleToDelete(null);
      } else {
        alert('เกิดข้อผิดพลาดในการลบข้อมูล: ' + result.error);
      }
    } catch (error) {
      console.error('Error force deleting sale:', error);
      alert('เกิดข้อผิดพลาดในการลบข้อมูล');
    }
  };



  const handleViewDetails = (sale) => {
    try {
      if (!sale) {
        console.error('Sale data is null or undefined');
        alert('ไม่สามารถแสดงรายละเอียดได้ ข้อมูลการขายไม่ถูกต้อง');
        return;
      }

      console.log('Opening sale details for:', sale);
      setSelectedSale(sale);
      setShowDetailsModal(true);
    } catch (error) {
      console.error('Error opening sale details:', error);
      alert('เกิดข้อผิดพลาดในการแสดงรายละเอียด');
    }
  };

  const showSerialNumbers = (serials) => {
    setSelectedSerials(serials);
    setShowSerialPopup(true);
  };

  return (
    <div className="container-fluid p-4">
      {/* Header */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="d-flex justify-content-between align-items-center">
            <h2 className="text-primary mb-0">
              <i className="fas fa-shopping-cart me-2"></i>
              จัดการการขาย
            </h2>
            <div>
              <button
                className="btn btn-secondary me-2"
                onClick={() => setShowDeletionLogModal(true)}
                title="ประวัติการลบข้อมูล"
              >
                <i className="fas fa-history"></i>
              </button>
              <button
                className="btn btn-primary"
                onClick={() => {
                  setEditingSale(null);
                  setShowSalesForm(true);
                }}
              >
                <i className="fas fa-plus me-2"></i>
                เพิ่มรายการขาย
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Search Filters */}
      <div className="row mb-4">
        <div className="col-12">
          <div className="card">
            <div className="card-header">
              <h5 className="mb-0">
                <i className="fas fa-search me-2"></i>
                ค้นหาและกรองข้อมูล
              </h5>
            </div>
            <div className="card-body">
              <div className="row g-3">
                <div className="col-md-3">
                  <label className="form-label">เลขที่ใบขาย/ใบเสนอราคา</label>
                  <input
                    type="text"
                    className="form-control"
                    name="saleId"
                    value={searchFilters.saleId}
                    onChange={handleSearchChange}
                    placeholder="ค้นหาเลขที่..."
                  />
                </div>
                <div className="col-md-3">
                  <label className="form-label">ชื่อลูกค้า</label>
                  <input
                    type="text"
                    className="form-control"
                    name="customerName"
                    value={searchFilters.customerName}
                    onChange={handleSearchChange}
                    placeholder="ค้นหาชื่อลูกค้า..."
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">วันที่เริ่มต้น</label>
                  <input
                    type="date"
                    className="form-control"
                    name="dateFrom"
                    value={searchFilters.dateFrom}
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">วันที่สิ้นสุด</label>
                  <input
                    type="date"
                    className="form-control"
                    name="dateTo"
                    value={searchFilters.dateTo}
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">วันที่ส่งมอบ เริ่มต้น</label>
                  <input
                    type="date"
                    className="form-control"
                    name="deliveryDateFrom"
                    value={searchFilters.deliveryDateFrom}
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">วันที่ส่งมอบ สิ้นสุด</label>
                  <input
                    type="date"
                    className="form-control"
                    name="deliveryDateTo"
                    value={searchFilters.deliveryDateTo}
                    onChange={handleSearchChange}
                  />
                </div>
                <div className="col-md-2">
                  <label className="form-label">สถานะ</label>
                  <select
                    className="form-select"
                    name="status"
                    value={searchFilters.status}
                    onChange={handleSearchChange}
                  >
                    <option value="all">ทั้งหมด</option>
                    <option value="สำเร็จ">สำเร็จ</option>
                    <option value="รอดำเนินการ">รอดำเนินการ</option>
                    <option value="ยกเลิก">ยกเลิก</option>
                  </select>
                </div>
              </div>
              <div className="row g-3 mt-2">
                <div className="col-md-3">
                  <label className="form-label">ประเภทสินค้า</label>
                  <select
                    className="form-select"
                    name="productType"
                    value={searchFilters.productType}
                    onChange={handleSearchChange}
                  >
                    <option value="all">ทั้งหมด</option>
                    <option value="battery">แบตเตอรี่</option>
                    <option value="charger">เครื่องชาร์จ</option>
                    <option value="accessory">อุปกรณ์เสริม</option>
                    <option value="service">บริการ</option>
                  </select>
                </div>
                <div className="col-md-9 d-flex align-items-end">
                  <button
                    className="btn btn-outline-secondary me-2"
                    onClick={clearFilters}
                  >
                    <i className="fas fa-times me-2"></i>
                    ล้างตัวกรอง
                  </button>
                  <button
                    className="btn btn-outline-primary me-2"
                    onClick={() => fetchSales()}
                  >
                    <i className="fas fa-sync-alt me-2"></i>
                    รีเฟรช
                  </button>
                  <button
                    className="btn btn-outline-warning"
                    onClick={() => {
                      console.log('Force refresh: Current sales state:', sales);
                      window.location.reload();
                    }}
                  >
                    <i className="fas fa-redo me-2"></i>
                    Force Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Sales List */}
      <div className="row">
        <div className="col-12">
          <SalesList
            sales={filteredSales}
            onEdit={handleEditSale}
            onDelete={handleDeleteSale}
            onRefresh={fetchSales}
            onViewDetails={handleViewDetails}
            onForceDelete={handleForceDelete}
          />
        </div>
      </div>

      {/* Sales Form Modal */}
      {showSalesForm && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingSale ? 'แก้ไขรายการขาย' : 'เพิ่มรายการขาย'}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setShowSalesForm(false);
                    setEditingSale(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <SalesForm
                  sale={editingSale}
                  onSave={editingSale ? handleUpdateSale : handleCreateSale}
                  onCancel={() => {
                    setShowSalesForm(false);
                    setEditingSale(null);
                  }}
                  customers={customers}
                  batteries={batteryStock}
                  chargers={chargerStock}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Serial Numbers Popup */}
      {showSerialPopup && (
        <div className="modal show d-block" tabIndex="-1" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">หมายเลขซีเรียล</h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowSerialPopup(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="list-group">
                  {selectedSerials.map((serial, index) => (
                    <div key={index} className="list-group-item">
                      <strong>#{index + 1}:</strong> {serial}
                    </div>
                  ))}
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowSerialPopup(false)}
                >
                  ปิด
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Sale Details Modal */}
      <SaleDetailsModal
        sale={selectedSale}
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedSale(null);
        }}
        onUpdate={() => {
          fetchSales();
          setSelectedSale(null);
          setShowDetailsModal(false);
        }}
      />

      <DeletionLogModal
        isOpen={showDeletionLogModal}
        onClose={() => setShowDeletionLogModal(false)}
        currentUser={currentUser}
      />

      <ConfirmDeleteModal
        isOpen={showConfirmDeleteModal}
        onClose={() => {
          setShowConfirmDeleteModal(false);
          setSaleToDelete(null);
        }}
        onConfirm={confirmForceDelete}
        title="ยืนยันการลบรายการขาย (Force Delete)"
        message="การลบแบบ Force จะลบข้อมูลการขาย รวมถึงข้อมูล PM และการตรวจสภาพที่เกี่ยวข้องทั้งหมด การกระทำนี้ไม่สามารถเรียกคืนได้"
        itemName={saleToDelete ? `รายการขาย: ${saleToDelete.sale_number || saleToDelete.id} - ${saleToDelete.customer_name || 'ไม่ระบุลูกค้า'}` : ''}
      />
    </div>
  );
};

export default SalePage;